import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { shallow } from 'zustand/shallow';
import { cloneDeep } from 'lodash';

import { AlbumPathListTemplate } from '../../components/Templates';
import {
  GameTypeHelpModal,
  GameTypeHelpType,
  LoadingSpinner,
  RewardStationCellState,
  StationCellProps,
  StationCellType,
  stringToGameTypeHelpType,
} from '../../components/Molecules';

import useFetchList, { FetchListFn } from '../../hooks/useFetchList';
import { useUserStore } from '../../stores/UserStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useGameStore, useGameStoreListField } from '../../stores/GameStore';
import { useSpaceStore, useSpaceStoreListField } from '../../stores/SpaceStore';
import {
  useRecordStore,
  useRecordStoreListField,
  useRecordStoreMapField,
} from '../../stores/RecordStore';
import { SpaceListType } from '../../models/SpaceStore';
import { PartKey } from '../../models/Base';
import { arrayEqual } from '../../utils/array';
import { getTimeString } from '../../utils/date';
import { computeReward } from '../../utils/records';
import {
  StationEmptyItemNo,
  StationGameItemNo,
  StationGoalItemNo,
  StationMiniChallengeItemNo,
  StationRewardItemNo,
  StationStartItemNo,
  StationSuperChallengeItemNo,
  StationTutorGameItemNo,
  StationType,
} from '../../constants/Games/gameStation';
import { SpaceType } from '../../constants/space';
import { DEFAULT_TRY_CLASSROOMID } from '../../constants/appInfo';
import { SpaceMembershipOperation } from '../../api/Rest/space';

const { fetchSpace, fetchAlbum, changeUserSpaceMembership } =
  useSpaceStore.getState();
const { fetchGameCollectionsByIds, fetchGame, fetchGameStatistics } =
  useGameStore.getState();
const { fetchAlbumSummaries } = useRecordStore.getState();

const AlbumPathListScreen: React.FC = () => {
  const navigate = useNavigate();
  const { classroomId, gameSetId } = useParams();

  const [popupType, setPopupType] = useState<GameTypeHelpType>();
  const [isLoading, setIsLoading] = useState(true);

  const activeUserId = useSessionStore((store) => store.activeUserId);

  const userAccount = useUserStore((state) =>
    state.getAccount(activeUserId ?? ''),
  );
  const userProfile = useUserStore((state) =>
    state.getProfile(activeUserId ?? ''),
  );

  useEffect(() => {
    const position = useGameStore.getState().getAlbumScrollPosition();
    if (position !== null && !isLoading) {
      setTimeout(() => {
        window.scrollTo(0, position);
      }, 0);
      useGameStore.getState().setAlbumScrollPosition(null);
    }
  }, [isLoading]);

  useEffect(() => {
    if (!activeUserId || !userProfile) {
      navigate('/start');
    }
  }, [activeUserId, navigate, userProfile]);

  useEffect(() => {
    if (
      userProfile?.worksForJoyajoy ||
      (userAccount?.managedClassroomExpiredAt &&
        userAccount?.managedClassroomExpiredAt > new Date()) ||
      classroomId === DEFAULT_TRY_CLASSROOMID
    ) {
      // do nothing
    } else {
      navigate('/smart-parents');
    }
  }, [
    classroomId,
    navigate,
    userAccount?.managedClassroomExpiredAt,
    userProfile?.worksForJoyajoy,
  ]);

  useEffect(() => {
    if (classroomId) {
      if (classroomId === DEFAULT_TRY_CLASSROOMID) {
        fetchSpace(SpaceListType.Managed, classroomId).catch(console.error);
      } else {
        fetchSpace(SpaceListType.Recommended, classroomId).catch(console.error);
      }
    }
  }, [classroomId]);

  const { managedClassroom, managedClassroomMembership } = useSpaceStore(
    (store) => {
      const space = store.getSpace(classroomId ?? '');
      const membership = store.getActiveUserMembership(classroomId ?? '');
      return {
        managedClassroom: space,
        managedClassroomMembership: membership,
      };
    },
    shallow,
  );

  // TODO: need to be async?
  const visitClassroom = useCallback(() => {
    changeUserSpaceMembership(classroomId ?? '', {
      operation: SpaceMembershipOperation.Visit,
    }).catch(console.error);
  }, [classroomId]);

  useEffect(() => {
    if (managedClassroom && managedClassroomMembership !== undefined) {
      if (managedClassroom.classroomId === DEFAULT_TRY_CLASSROOMID) {
        setIsLoading(false);
        if (managedClassroomMembership === null) {
          visitClassroom();
        }
      } else {
        if (managedClassroom.type !== SpaceType.Managed) {
          useSpaceStore.getState().resetSpacesList(SpaceListType.Managed);
          navigate('/smart-parents');
        }
        if (managedClassroomMembership?.isAtLeastMember) {
          setIsLoading(false);
        } else {
          useSpaceStore.getState().resetSpacesList(SpaceListType.Managed);
          navigate('/smart-parents');
        }
      }
    }
  }, [
    managedClassroom,
    managedClassroomMembership,
    managedClassroomMembership?.isAtLeastMember,
    navigate,
    visitClassroom,
  ]);

  useEffect(() => {
    if (classroomId && gameSetId) {
      fetchAlbum(
        { classroomId, gameSetId },
        { headers: { 'cache-control': 'no-cache' } },
      ).catch(console.error);
    }
  }, [classroomId, gameSetId]);

  const album = useSpaceStore(
    (store) => store.getAlbum(gameSetId ?? ''),
    shallow,
  );

  useSpaceStoreListField(
    'spaces',
    classroomId === DEFAULT_TRY_CLASSROOMID
      ? SpaceListType.Recommended
      : SpaceListType.Managed,
  );
  useGameStoreListField('gameCollections', `album_${album?.gameSetId}`);

  const gameCollectionIdsJoin = album?.content?.gameCollectionIds.join('_');
  useGameStoreListField('games', `${gameCollectionIdsJoin}`);
  useGameStoreListField('gameStatistics', gameSetId);
  useRecordStoreMapField('albumSummaries', `album_${gameSetId}`);

  useEffect(() => {
    if (album?.content?.gameCollectionIds && album?.gameSetId) {
      const gameCollectionIds = album?.content?.gameCollectionIds;
      if (gameCollectionIds) {
        fetchGameCollectionsByIds({
          fieldName: `album_${album?.gameSetId}`,
          gameCollectionIds: gameCollectionIds,
        }).catch(console.error);
      }
    }
  }, [album?.content?.gameCollectionIds, album?.gameSetId]);

  useEffect(() => {
    if (album?.gameSetId) {
      fetchAlbumSummaries({ gameSetIds: [album?.gameSetId] }).catch(
        console.error,
      );
    }
  }, [album?.gameSetId]);

  const albumSummary = useRecordStore((store) =>
    store.getAlbumSummary(gameSetId ?? ''),
  );

  const gameItems = useGameStore((state) => {
    const gameCollections = (album?.content?.gameCollectionIds ?? []).map(
      (id) => state.getGameCollection(id),
    );
    if (
      gameCollections.some((gameCollection) => gameCollection === undefined)
    ) {
      // only return data if all game collection loaded
      return undefined;
    }

    return gameCollections.flatMap(
      (gameCollection) =>
        gameCollection?.getPartItemArray((item) => item.game) ?? [],
    );
  }, arrayEqual(shallow));

  const games = useGameStore((state) => {
    return gameItems?.map((gameItem) =>
      state.getGame(gameItem?.game?.gameId ?? ''),
    );
  }, arrayEqual(shallow));

  const gameRecordFieldName = `${classroomId}_${gameSetId}`;
  const gameRecordField = useRecordStoreListField(
    'gameRecords',
    gameRecordFieldName,
  );

  const fetchGameRecords = useCallback<FetchListFn>(
    (cursor) =>
      useRecordStore.getState().fetchGameRecords(
        {
          fieldName: gameRecordFieldName,
          classroomId: classroomId ?? '',
          playedBy: activeUserId ?? '',
          gameSetId: gameSetId ?? '',
        },
        cursor,
        { headers: { 'cache-control': 'no-cache' } },
      ),
    [activeUserId, classroomId, gameRecordFieldName, gameSetId],
  );

  const { fetchAll: fetchAllGameRecords } = useFetchList(
    gameRecordField,
    fetchGameRecords,
  );

  useEffect(() => {
    if (gameItems) {
      fetchAllGameRecords().catch(console.error);
    }
  }, [fetchAllGameRecords, gameItems]);

  const allGameRecords = useRecordStore((state) => {
    return state.getGameRecords(gameRecordFieldName);
  }, arrayEqual(shallow));

  const challengeRecordFieldName = `${classroomId}_${gameSetId}`;
  const challengeRecordField = useRecordStoreListField(
    'challengeRecords',
    challengeRecordFieldName,
  );

  const fetchChallengeRecords = useCallback<FetchListFn>(
    (cursor) =>
      useRecordStore.getState().fetchChallengeRecords(
        {
          fieldName: challengeRecordFieldName,
          classroomId: classroomId ?? '',
          gameSetId: gameSetId ?? '',
        },
        cursor,
      ),
    [challengeRecordFieldName, classroomId, gameSetId],
  );

  const { fetchAll: fetchAllChallengeRecords } = useFetchList(
    challengeRecordField,
    fetchChallengeRecords,
  );

  useEffect(() => {
    if (gameItems) {
      fetchAllChallengeRecords().catch(console.error);
    }
  }, [fetchAllChallengeRecords, gameItems]);

  const allChallengeRecords = useRecordStore((state) => {
    return state.getChallengeRecords(challengeRecordFieldName);
  }, arrayEqual(shallow));

  useEffect(() => {
    if (gameItems && album) {
      gameItems.forEach((item) => {
        const gameCollectionId = item.gameCollectionId;
        const gameId = item.game?.gameId;
        const hash = item.game?.hash;
        if (gameId && hash) {
          fetchGame({
            fieldName: gameCollectionIdsJoin ?? '',
            gameCollectionId,
            gameId,
            hash,
          }).catch(console.error);
          fetchGameStatistics(
            { fieldName: album.gameSetId, gameCollectionId, gameId, hash },
            { headers: { 'cache-control': 'no-cache' } },
          ).catch(console.error);
        }
      });
    }
  }, [album, gameCollectionIdsJoin, gameItems]);

  const allGameStatistics = useGameStore(
    (store) => store.getGameStatisticsList(gameSetId ?? ''),
    arrayEqual(shallow),
  );

  const stationsData = useMemo<
    (StationCellProps & { isCompleted: boolean })[]
  >(() => {
    if (album && managedClassroom && userProfile && games) {
      const stations = album.content?.path
        .flatMap((path) => path.stations)
        .filter(
          (station) =>
            station.type === StationType.Start ||
            station.type === StationType.Game ||
            station.type === StationType.AssistedGame ||
            station.type === StationType.Goal,
        );
      const stationStyle = album.content?.path[0]?.style;
      let currentGameIndex = 0;
      let currentAssistedGameIndex = 0;
      // const challengeGameIndex = 0;
      if (stations) {
        return stations.map((station, index) => {
          const name =
            station?.i18nStationInfo?.resources[
              station?.i18nStationInfo?.defaultLocale
            ]?.name;
          const isUnlocked =
            (albumSummary?.path?.unlockedStations ?? 0) >= index;
          switch (station.type) {
            case StationType.Start:
              return {
                type: StationCellType.Start,
                props: {
                  album: album,
                  albumClassroom: managedClassroom,
                  stationId:
                    stationStyle?.stations.start ?? StationStartItemNo.s1,
                  name: name,
                  isUnlocked: true,
                },
                isCompleted: true,
              };
            case StationType.Game: {
              const gameItem = gameItems?.[currentGameIndex];
              const game = games?.find(
                (g) => g?.gameId === gameItem?.game?.gameId,
              );
              const gameStatistics = allGameStatistics.find(
                (stats) => stats.gameId === gameItem?.game?.gameId,
              );
              const gameRecords = allGameRecords.filter(
                (record) =>
                  record.gameId === gameItem?.game?.gameId && record.isNormal,
              );
              const lastGameRecord = gameRecords.find(
                (record) => record.completed,
              );
              const remainingReward = computeReward(
                managedClassroomMembership?.isAtLeastMember,
                gameItem?.gameReward ?? {},
                false,
                5,
                gameRecords?.map((record) => record.reward) ?? [],
                1,
                gameItem?.game?.answerSlots,
              ).reward;
              const canPassStation = gameRecords?.find(
                (record) => record.canPassStation,
              );
              currentGameIndex = currentGameIndex + 1;
              if (game) {
                return {
                  type: StationCellType.Game,
                  props: {
                    albumClassroom: managedClassroom,
                    game: game,
                    stationId:
                      stationStyle?.stations.game ?? StationGameItemNo.s1,
                    name: name,
                    index: index,
                    iconKey: gameItem?.icon?.tag ?? '',
                    playCount: gameStatistics?.statistics.playCount ?? 0,
                    averageScore: gameStatistics?.averageScore ?? 0,
                    userProfile: userProfile,
                    remainingSpaceReward: 0, //remainingReward.classroomCredit,
                    remainingJCoinReward: remainingReward.joyPoint,
                    recordScore: lastGameRecord?.score,
                    recordDuration: lastGameRecord?.durationInMs
                      ? getTimeString(
                          lastGameRecord?.durationInMs,
                          undefined,
                          true,
                        )
                      : undefined,
                    isUnlocked: isUnlocked,
                    onClickPlay: () => {
                      const scrollPosition =
                        window.scrollY || window.pageYOffset;
                      useGameStore
                        .getState()
                        .setAlbumScrollPosition(scrollPosition);
                      console.log(scrollPosition);
                      useRecordStore
                        .getState()
                        .moveInAlbumSummaryPath(
                          managedClassroom.classroomId,
                          album.gameSetId,
                          index,
                        )
                        .catch(() => {
                          console.error();
                        });
                      navigate(
                        `/club/${managedClassroom.classroomId}/journey/${album.gameSetId}/game/${game.gameId}`,
                      );
                    },
                    onClickInfo: () => {
                      setPopupType(
                        stringToGameTypeHelpType(gameItem?.icon?.tag),
                      );
                    },
                  },
                  isCompleted: !!canPassStation,
                };
              }
              return {
                type: StationCellType.Empty,
                props: {
                  stationId:
                    stationStyle?.stations.empty ?? StationEmptyItemNo.s1,
                  name: name,
                  isUnlocked: isUnlocked,
                },
                isCompleted: true,
              };
            }
            case StationType.AssistedGame: {
              const gameItem = gameItems?.[currentAssistedGameIndex];
              const game = games?.find(
                (g) => g?.gameId === gameItem?.game?.gameId,
              );
              const gameStatistics = allGameStatistics.find(
                (stats) => stats.gameId === gameItem?.game?.gameId,
              );
              const gameRecords = allGameRecords.filter(
                (record) =>
                  record.gameId === gameItem?.game?.gameId && record.isAssisted,
              );
              const lastGameRecord = gameRecords.find(
                (record) => record.completed,
              );
              currentAssistedGameIndex = currentAssistedGameIndex + 1;
              const canPassStation = gameRecords?.find(
                (record) => record.canPassStation,
              );
              if (game) {
                return {
                  type: StationCellType.Assisted,
                  props: {
                    game: game,
                    stationId:
                      stationStyle?.stations.assistedGame ??
                      StationTutorGameItemNo.s1,
                    name: name,
                    index: index,
                    iconKey: gameItem?.icon?.tag ?? '',
                    playCount:
                      gameStatistics?.statistics.assistedPlayCount ?? 0,
                    userProfile: userProfile,
                    recordScore: lastGameRecord?.score,
                    recordDuration: lastGameRecord?.durationInMs
                      ? getTimeString(
                          lastGameRecord?.durationInMs,
                          undefined,
                          true,
                        )
                      : undefined,
                    isUnlocked: isUnlocked,
                    onClickPlay: () => {
                      useRecordStore
                        .getState()
                        .moveInAlbumSummaryPath(
                          managedClassroom.classroomId,
                          album.gameSetId,
                          index,
                        )
                        .catch(() => {
                          console.error();
                        });
                      navigate(
                        `/club/${managedClassroom.classroomId}/journey/${album.gameSetId}/game/${game.gameId}?mode=tutorial`,
                      );
                    },
                    onClickInfo: () => {
                      setPopupType(stringToGameTypeHelpType('assisted'));
                    },
                  },
                  isCompleted: !!canPassStation,
                };
              }
              return {
                type: StationCellType.Empty,
                props: {
                  stationId:
                    stationStyle?.stations.empty ?? StationEmptyItemNo.s1,
                  name: name,
                  isUnlocked: isUnlocked,
                },
                isCompleted: true,
              };
            }
            // case StationType.PathChallenge: {
            //   const partId: PartKey = String(challengeGameIndex).padStart(
            //     2,
            //     '0',
            //   ) as PartKey;
            //   const playCount =
            //     album.statistics[`pathChallenge_${partId}PlayCount`] ?? 0;
            //   const scoreTotal =
            //     album.statistics[`pathChallenge_${partId}ScoreTotal`] ?? 0;
            //   const averageScore = playCount ? scoreTotal / playCount : 0;
            //   const challengeGameRecords = allChallengeRecords.filter(
            //     (record) => record.type === `pathChallenge_${partId}`,
            //   );
            //   const lastChallengeGameRecord = challengeGameRecords
            //     .find((record) => record.completed);
            //   // const remainingReward = computeReward(
            //   //   managedClassroomMembership?.isAtLeastMember,
            //   //   {}, // TODO
            //   //   false,
            //   //   5,
            //   //   challengeGameRecords?.map((record) => record.reward) ?? [],
            //   //   1,
            //   //   gameItem?.game?.answerSlots,
            //   // ).reward;
            //   challengeGameIndex = challengeGameIndex + 1;
            //   const canPassStation = challengeGameRecords?.find(
            //     (record) => record.canPassStation,
            //   );
            //   return {
            //     type: StationCellType.Challenge,
            //     props: {
            //       stationId:
            //         stationStyle?.stations.miniChallenge ??
            //         StationMiniChallengeItemNo.s1,
            //       albumClassroom: managedClassroom,
            //       name: name,
            //       index: index,
            //       playCount: playCount,
            //       averageScore: averageScore,
            //       userProfile: userProfile,
            //       remainingSpaceReward: undefined,
            //       remainingJCoinReward: undefined,
            //       recordScore: lastChallengeGameRecord?.score,
            //       recordDuration: lastChallengeGameRecord?.durationInMs
            //         ? getTimeString(
            //             lastChallengeGameRecord?.durationInMs,
            //             undefined,
            //             true,
            //           )
            //         : undefined,
            //       isMiniChallenge: true,
            //       isUnlocked: isUnlocked,
            //       onClickPlay: () => {
            //         console.log('play isMiniChallenge', partId);
            //       },
            //     },
            //     isCompleted: !!canPassStation,
            //   };
            // }
            // case StationType.SuperChallenge: {
            //   const playCount = album.statistics.superChallengePlayCount ?? 0;
            //   const scoreTotal = album.statistics.superChallengeScoreTotal ?? 0;
            //   const averageScore = playCount ? scoreTotal / playCount : 0;
            //   const challengeGameRecords = allChallengeRecords.filter(
            //     (record) => record.type === `superChallenge`,
            //   );
            //   const lastChallengeGameRecord = challengeGameRecords
            //     .find((record) => record.completed);
            //   const canPassStation = challengeGameRecords?.find(
            //     (record) => record.canPassStation,
            //   );
            //   return {
            //     type: StationCellType.Challenge,
            //     props: {
            //       stationId:
            //         stationStyle?.stations.superChallenge ??
            //         StationSuperChallengeItemNo.s1,
            //       albumClassroom: managedClassroom,
            //       name: name,
            //       index: index,
            //       playCount: playCount,
            //       averageScore: averageScore,
            //       userProfile: userProfile,
            //       remainingSpaceReward: undefined,
            //       remainingJCoinReward: undefined,
            //       recordScore: lastChallengeGameRecord?.score,
            //       recordDuration: lastChallengeGameRecord?.durationInMs
            //         ? getTimeString(
            //             lastChallengeGameRecord?.durationInMs,
            //             undefined,
            //             true,
            //           )
            //         : undefined,
            //       isMiniChallenge: false,
            //       isUnlocked: isUnlocked,
            //       onClickPlay: () => {
            //         console.log('play isSuperChallenge');
            //       },
            //     },
            //     isCompleted: !!canPassStation,
            //   };
            // }
            // case StationType.Reward: {
            //   const isCollectedReward =
            //     !!albumSummary?.path?.collectedJoyBallAt;
            //   return {
            //     type: StationCellType.Reward,
            //     props: {
            //       stationId:
            //         stationStyle?.stations.reward ?? StationRewardItemNo.s1,
            //       state: isCollectedReward
            //         ? RewardStationCellState.CollectedRewards
            //         : RewardStationCellState.Uncompleted,
            //       name: name,
            //       isUnlocked: isUnlocked,
            //       onClickPlay: () => {
            //         console.log('play isReward');
            //       },
            //     },
            //     isCompleted: isCollectedReward,
            //   };
            // }
            case StationType.Goal: {
              const isCollectedReward =
                !!albumSummary?.path?.collectedStarPointAt;
              return {
                type: StationCellType.Goal,
                props: {
                  stationId:
                    stationStyle?.stations.goal ?? StationGoalItemNo.s1,
                  name: name,
                  isUnlocked: isUnlocked,
                  onClickPlay: () => {
                    console.log('play isGoal');
                  },
                },
                isCompleted: isCollectedReward,
              };
            }
            default: {
              return {
                type: StationCellType.Empty,
                props: {
                  stationId:
                    stationStyle?.stations.empty ?? StationEmptyItemNo.s1,
                  name: name,
                  isUnlocked: isUnlocked,
                },
                isCompleted: true,
              };
            }
          }
        });
      }
    }
    return [];
  }, [
    album,
    albumSummary?.path?.collectedStarPointAt,
    albumSummary?.path?.unlockedStations,
    allGameRecords,
    allGameStatistics,
    gameItems,
    games,
    managedClassroom,
    managedClassroomMembership?.isAtLeastMember,
    navigate,
    userProfile,
  ]);

  const stations = useMemo<StationCellProps[]>(() => {
    return stationsData
      .filter((station) => station.type !== StationCellType.Empty)
      .map((_station, index, arr) => {
        const station = cloneDeep(_station);
        if (index > 0) {
          const stationData = arr[index - 1];
          if (stationData) {
            station.props.isUnlocked =
              station.props.isUnlocked || stationData.isCompleted;
            if (!station.props.isUnlocked) {
              station.props.onClickPlay = () => {};
            }
            if (station.type === StationCellType.Reward) {
              if (station.props.state === RewardStationCellState.Uncompleted) {
                station.props.state = stationData.isCompleted
                  ? RewardStationCellState.AbleGetReward
                  : RewardStationCellState.Uncompleted;
              }
            }
          }
        }
        return station;
      });
  }, [stationsData]);

  const albumInfo =
    album?.i18nGameSetInfo.resources[album?.i18nGameSetInfo.defaultLocale];
  const albumName = albumInfo?.name;

  return (
    <>
      {isLoading ? (
        <LoadingSpinner loading={isLoading} />
      ) : (
        <AlbumPathListTemplate
          albumName={albumName}
          pathStyleBackground={
            album?.content?.path[0]?.style.background.set ?? 0
          } // TODO: may also assign image
          stations={stations.reverse()}
        />
      )}

      {popupType ? (
        <GameTypeHelpModal
          type={popupType}
          onClose={() => {
            setPopupType(undefined);
          }}
        />
      ) : null}
    </>
  );
};

export default AlbumPathListScreen;
